import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import { Delta } from 'quill/core';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import PublicTopicPosts from './PublicTopicPosts';
import * as styles from './PublicTopicView.module.scss';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import PageLayout from '../app/layout/PageLayout';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Caret from '../components/Caret';
import DeltaPreview from '../components/DeltaPreview';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import SummaryCollapsibleContent from '../components/SummaryCollapsibleContent';
import parseBlockEmbed from '../editor/utils/parseBlockEmbed';
import NotFoundView from '../error/NotFoundView';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';
import { getFirstLineFromDelta } from '../utils/deltaHelpers';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import PageMeta from '../utils/PageMeta';
import Link from '../utils/router/Link';
import { parseSlug } from '../utils/router/useSlug';
import topicPath, { publicTopicPath } from '../utils/url/topicPath';

const PUBLIC_TOPIC_QUERY = gql(/** @complexity 26 */ `
  query PublicTopicView($id: ID!, $afterCursor: String) {
    topic: publicTopic(id: $id) {
      id
      name
      description
      ...topicPath_topic
      posts {
        id
        ...PublicTopicPosts_posts
      }
      banner {
        original
      }
      descendants(after: $afterCursor, first: 10) {
        edges {
          node {
            id
            name
            description
            posts {
              id
              ...PublicTopicPosts_posts
            }
            ancestors {
              id
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      ancestors {
        id
        ...TopicBreadcrumbs_topics
      }
    }
  }
`);

interface PublicTopicViewContentProps {
  topic: DocumentType<typeof PUBLIC_TOPIC_QUERY>['topic'];
  onFetchMore: (() => void) | null;
}

const PublicTopicViewContent = ({
  topic,
  onFetchMore,
}: PublicTopicViewContentProps) => {
  const edges = _.compact(topic.descendants?.edges);
  const descendants = _.compact(edges.map((edge) => edge?.node));

  return (
    <>
      {topic.posts.length > 0 && (
        <div className={styles.posts}>
          <div className={styles.postsLabel}>Posts</div>
          <PublicTopicPosts posts={topic.posts} />
        </div>
      )}
      {descendants.map((descendant) => {
        const descendantsOfCurrent = edges.filter(({ node }) =>
          node?.ancestors
            .map((ancestor) => ancestor.id)
            .includes(descendant.id),
        );
        const isDescendantsOfCurrentHavePosts = descendantsOfCurrent.some(
          ({ node }) => node && node.posts.length > 0,
        );
        if (descendant.posts.length === 0 && !isDescendantsOfCurrentHavePosts)
          return null;

        const depth =
          descendant.ancestors
            .map((ancestor) => ancestor.id)
            .indexOf(topic.id) + 1;
        let className;
        switch (depth) {
          case 1:
            className = styles.section;
            break;
          case 2:
            className = styles.subSection;
            break;
          case 3:
            className = styles.subSubSection;
            break;
          default:
            className = styles.subSubSubSection;
            break;
        }

        const descriptionFirstLine = getFirstLineFromDelta(
          new Delta(descendant.description),
        );

        const isBlockEmbed =
          descriptionFirstLine?.ops[0] &&
          parseBlockEmbed(descriptionFirstLine.ops[0])[0] != null;

        return (
          <div key={descendant.id} className={className}>
            <Link
              className={styles.sectionHeader}
              to={publicTopicPath(descendant)}
            >
              <div className={styles.sectionTitle}>{descendant.name}</div>
              <div className={styles.caret}>
                <Caret direction="right" size="small" block />
              </div>
            </Link>

            {!isBlockEmbed && descriptionFirstLine && (
              <div className={styles.sectionDescription}>
                <DeltaPreview
                  content={descriptionFirstLine?.ops}
                  typography="postWithNoTitleLine"
                />
              </div>
            )}

            {descendant.posts.length > 0 && (
              <div className={styles.sectionContent}>
                <PublicTopicPosts posts={descendant.posts} />
              </div>
            )}
          </div>
        );
      })}

      <InfiniteScrollWaypoint fetchMore={onFetchMore} />
    </>
  );
};

const PublicTopicView = () => {
  const { topicSlug } = useParams();
  const dispatch = useAppDispatch();

  const [id] = parseSlug(topicSlug ?? '');
  const response = useQuery(PUBLIC_TOPIC_QUERY, {
    variables: { id },
  });
  const { data: { topic } = {}, loading } = response;
  const fetchMoreDescendants = ConnectionHelper.generateFetchMore(
    PUBLIC_TOPIC_QUERY,
    response,
    'topic.descendants',
  );

  const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(false);

  useEffect(() => {
    if (!topic) return undefined;

    dispatch(UIActions.setExternalTopbarLoginRedirectPath(topicPath(topic)));
    return () => {
      dispatch(UIActions.setExternalTopbarLoginRedirectPath());
    };
  }, [dispatch, topic]);

  if (!topic) {
    return loading ? null : <NotFoundView />;
  }

  return (
    <>
      <PageMeta title={`${topic.name} - ${window.__slab.PUBLIC_ORG.name}`} />
      <div className={styles.contentContainer}>
        {topic.banner.original && (
          <img
            className={styles.banner}
            src={topic.banner.original}
            alt="Topic banner"
          />
        )}
        <PageLayout.Content>
          <div className={styles.wrapper}>
            <div className={styles.container}>
              <div className={styles.topSection}>
                <div className={styles.titleContainer}>
                  {topic.ancestors.length > 0 && (
                    <TopicBreadcrumbs
                      topics={topic.ancestors}
                      link
                      showLastCaret
                    />
                  )}
                  <h1>{topic.name}</h1>
                </div>
              </div>
              {!!topic.description && (
                <h3 className={styles.description}>
                  <SummaryCollapsibleContent
                    collapsed={isDescriptionCollapsed}
                    onCollapsedChange={() =>
                      setIsDescriptionCollapsed(!isDescriptionCollapsed)
                    }
                    content={topic.description}
                    maxLines={6}
                    typography="postWithNoTitleLine"
                  />
                </h3>
              )}

              <PublicTopicViewContent
                topic={topic}
                onFetchMore={fetchMoreDescendants}
              />
            </div>
          </div>
        </PageLayout.Content>
      </div>
    </>
  );
};

export default PublicTopicView;
